import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCliente } from '../../hooks/useCliente';
import './CadastroClienteModal.css';

interface CadastroClienteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

export const CadastroClienteModal: React.FC<CadastroClienteModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  onSwitchToLogin
}) => {
  const { cadastrarCliente, loading, error } = useCliente();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    senha: ''
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
      // Reset form when closing
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        senha: ''
      });
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Máscara de telefone
    let formattedValue = value;
    if (name === 'telefone') {
      const numbers = value.replace(/\D/g, '');
      if (numbers.length <= 10) {
        formattedValue = numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
      } else {
        formattedValue = numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    
    // Limpar mensagem de erro quando o usuário começar a digitar
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateTelefone = (telefone: string): boolean => {
    const numbers = telefone.replace(/\D/g, '');
    return numbers.length >= 10 && numbers.length <= 11;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error message
    setErrorMessage('');
    setSubmitStatus('idle');

    // Validação de campos obrigatórios
    if (!formData.nome || !formData.email || !formData.telefone || !formData.senha) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    // Validação de nome (mínimo 2 caracteres)
    if (formData.nome.trim().length < 2) {
      setSubmitStatus('error');
      setErrorMessage('O nome deve ter no mínimo 2 caracteres.');
      return;
    }

    // Validação de email
    if (!validateEmail(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, insira um e-mail válido.');
      return;
    }

    // Validação de telefone
    if (!validateTelefone(formData.telefone)) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, insira um telefone válido.');
      return;
    }

    // Validação de senha (mínimo 6 caracteres)
    if (formData.senha.length < 6) {
      setSubmitStatus('error');
      setErrorMessage('A senha deve ter no mínimo 6 caracteres.');
      return;
    }

    setSubmitStatus('idle');

    try {
      await cadastrarCliente({
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        telefone: formData.telefone.trim(),
        senha: formData.senha
      });

      setSubmitStatus('success');
      
      // Limpar formulário após sucesso
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        senha: ''
      });

      // Fechar modal após 1.5 segundos
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      setSubmitStatus('error');
      let errorMsg = err?.message || 'Erro ao cadastrar. Por favor, tente novamente.';
      
      // Mensagem mais amigável para erro 404
      if (errorMsg.includes('não encontrada') || errorMsg.includes('404')) {
        errorMsg = 'Esta funcionalidade ainda não está disponível. O backend precisa implementar a rota de cadastro de clientes.';
      }
      
      setErrorMessage(errorMsg);
      console.error('Erro ao cadastrar cliente:', err);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="cadastro-cliente-modal-overlay" onClick={handleOverlayClick}>
      <div className="cadastro-cliente-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cadastro-cliente-modal-header">
          <h2>Cadastro Rápido</h2>
          <button 
            className="cadastro-cliente-modal-close"
            onClick={onClose}
            aria-label="Fechar modal"
          >
            ✕
          </button>
        </div>

        <div className="cadastro-cliente-modal-body">
          <p className="cadastro-cliente-modal-description">
            Faça seu cadastro rápido para salvar imóveis favoritos e receber novidades.
          </p>

          <form onSubmit={handleSubmit} className="cadastro-cliente-form">
            <div className="cadastro-cliente-form-group">
              <label htmlFor="nome">Nome *</label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                placeholder="Seu nome completo"
                required
                disabled={loading}
                className="cadastro-cliente-form-input"
                minLength={2}
              />
            </div>

            <div className="cadastro-cliente-form-group">
              <label htmlFor="email">E-mail *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                required
                disabled={loading}
                className="cadastro-cliente-form-input"
              />
            </div>

            <div className="cadastro-cliente-form-group">
              <label htmlFor="telefone">Telefone *</label>
              <input
                type="tel"
                id="telefone"
                name="telefone"
                value={formData.telefone}
                onChange={handleInputChange}
                placeholder="(00) 00000-0000"
                required
                disabled={loading}
                className="cadastro-cliente-form-input"
              />
            </div>

            <div className="cadastro-cliente-form-group">
              <label htmlFor="senha">Senha *</label>
              <input
                type="password"
                id="senha"
                name="senha"
                value={formData.senha}
                onChange={handleInputChange}
                placeholder="Mínimo 6 caracteres"
                required
                disabled={loading}
                className="cadastro-cliente-form-input"
                autoComplete="new-password"
                minLength={6}
              />
            </div>

            {submitStatus === 'success' && (
              <div className="cadastro-cliente-form-message cadastro-cliente-form-message-success">
                Cadastro realizado com sucesso!
              </div>
            )}

            {submitStatus === 'error' && errorMessage && (
              <div className="cadastro-cliente-form-message cadastro-cliente-form-message-error">
                {errorMessage}
              </div>
            )}

            {error && !errorMessage && (
              <div className="cadastro-cliente-form-message cadastro-cliente-form-message-error">
                {error}
              </div>
            )}

            <div className="cadastro-cliente-form-actions">
              <button
                type="button"
                onClick={onClose}
                className="cadastro-cliente-btn cadastro-cliente-btn-cancel"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="cadastro-cliente-btn cadastro-cliente-btn-submit"
                disabled={loading}
              >
                {loading ? 'Cadastrando...' : 'Cadastrar'}
              </button>
            </div>

            {onSwitchToLogin && (
              <div className="cadastro-cliente-form-footer">
                <p>
                  Já tem uma conta?{' '}
                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    className="cadastro-cliente-link"
                    disabled={loading}
                  >
                    Faça login
                  </button>
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

